import { ComponentCmsDataFormQchoice } from "@/generated";
import clsx from "clsx";
import { useEffect } from "react";

interface ChoiceButtonProps {
  choice: ComponentCmsDataFormQchoice;
  onSelect: (choice: ComponentCmsDataFormQchoice) => void;
  multiple?: boolean;
  isSelected?: boolean;
  isMobile?: boolean;
  isOtherSelected?: boolean;
  size?: "small" | "medium" | "large";
}

export const ChoiceButton: React.FC<ChoiceButtonProps> = ({
  choice,
  onSelect,
  isSelected,
  isMobile,
  isOtherSelected,
  size = "medium",
}) => {
  useEffect(() => {
    if (!choice.choice_shortcut || isMobile || isOtherSelected) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === choice.choice_shortcut?.toLowerCase()) {
        onSelect(choice);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [choice, onSelect, isMobile, isOtherSelected]);

  return (
    <button
      onClick={() => onSelect(choice)}
      className={clsx([
        "transition-all",
        choice.choice_big_button && size === "small" && "!text-base font-bold",
        choice.choice_big_button && size === "medium" && "!text-xl font-bold",
        choice.choice_big_button && size === "large" && "!text-2xl font-bold",
        !choice.choice_big_button && "text-sm font-medium w-full",
        choice.choice_media?.data?.attributes?.url
          ? "relative aspect-square rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-1"
          : clsx([
              "p-3 rounded-lg",
              "hover:shadow-lg hover:-translate-y-1",
              "flex items-center",
              choice.choice_color_transparent
                ? "border-2 border-transparent"
                : "",
              isSelected && "ring-2 ring-offset-2 ring-blue-500",
            ]),
      ])}
      style={
        choice.choice_media?.data?.attributes?.url
          ? {
              backgroundImage: `url(${choice.choice_media?.data?.attributes?.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              opacity: isSelected ? 1 : 0.8,
            }
          : choice.choice_color_transparent
            ? {
                backgroundColor: `${choice.choice_color || "#13235b"}50`,
                boxSizing: "border-box",
                border: `2px solid ${choice.choice_color || "#13235b"}`,
                opacity: isSelected ? 1 : "initial",
                transition: `all 0.3s ease-in`,
                visibility: choice.display_delay ? "hidden" : "visible",
                animation: choice.display_delay
                  ? `fadeIn 0.3s ease-in ${choice.display_delay}s forwards`
                  : "none",
              }
            : {
                backgroundColor: choice.choice_color || "#007bff",
                opacity: isSelected
                  ? 1
                  : choice.choice_color_transparent
                    ? 0.25
                    : 1,
                transition: `all 0.3s ease-in`,
                visibility: choice.display_delay ? "hidden" : "visible",
                animation: choice.display_delay
                  ? `fadeIn 0.3s ease-in ${choice.display_delay}s forwards`
                  : "none",
              }
      }
    >
      <span
        className={clsx([
          choice.choice_media?.data?.attributes?.url &&
            "absolute bottom-0 left-0 right-0 p-1 text-white",
          choice.choice_media?.data?.attributes?.url &&
            choice.choice_text &&
            "bg-black/50",
        ])}
      >
        {choice.choice_shortcut && !isMobile && !isOtherSelected && (
          <span className="mr-2 px-2 py-0.5 bg-white/20 rounded text-xs uppercase">
            {choice.choice_shortcut}
          </span>
        )}
        {choice.choice_text}
      </span>
    </button>
  );
};
